/**
 * PricingDisplay Component
 * Displays pricing information after service area validation
 * Supports both fixed rates (franchise) and dynamic quotes (spot)
 */

import { useState } from 'react';
import { DollarSign, Clock, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';

interface FixedRates {
  '10Y': number;
  '20Y': number;
  '30Y': number;
  '40Y': number;
}

interface DynamicQuote {
  estimatedPriceCents: number;
  breakdown: {
    baseCostCents: number;
    distanceCostCents: number;
    timeCostCents: number;
    markupPercent: number;
  };
  estimatedDriveMinutes: number;
  estimatedDistanceMiles: number;
}

interface PricingDisplayProps {
  type: 'fixed' | 'dynamic';
  fixedRates?: FixedRates;
  dynamicQuote?: DynamicQuote;
}

/**
 * Format cents to dollar string with proper formatting
 */
const formatCents = (cents: number): string => {
  const dollars = cents / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(dollars);
};

/**
 * Get container size label from code
 */
const getContainerLabel = (size: keyof FixedRates): string => {
  const labels: Record<keyof FixedRates, string> = {
    '10Y': '10 Yard',
    '20Y': '20 Yard',
    '30Y': '30 Yard',
    '40Y': '40 Yard',
  };
  return labels[size];
};

/**
 * FixedRatesPricing - Display fixed rates table
 */
function FixedRatesPricing({ rates }: { rates: FixedRates }) {
  const containerSizes: (keyof FixedRates)[] = ['10Y', '20Y', '30Y', '40Y'];

  return (
    <Card className="border-green-200 bg-green-50">
      <CardContent className="p-6">
        {/* Header */}
        <div className="flex items-start gap-3 mb-4">
          <div className="p-2 bg-green-100 rounded-lg">
            <DollarSign className="h-5 w-5 text-green-700" aria-hidden="true" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-lg font-semibold text-green-900">
                Available Pricing
              </h3>
              <Badge variant="success" className="bg-green-700">
                Verified
              </Badge>
            </div>
            <p className="text-sm text-green-800">
              City-mandated fixed rates - locked pricing
            </p>
          </div>
        </div>

        {/* Pricing Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {containerSizes.map((size) => (
            <div
              key={size}
              className="bg-white border border-green-200 rounded-lg p-4 text-center hover:border-green-300 transition-colors"
            >
              <div className="text-sm font-medium text-gray-700 mb-2">
                {getContainerLabel(size)}
              </div>
              <div className="text-xl font-bold text-green-700">
                {formatCents(rates[size])}
              </div>
            </div>
          ))}
        </div>

        {/* Info Note */}
        <div className="mt-4 flex items-start gap-2 text-xs text-green-800 bg-green-100 rounded-lg p-3">
          <Info className="h-4 w-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
          <p>
            These rates are set by the local jurisdiction and include delivery, rental, and pickup.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * DynamicQuotePricing - Display dynamic quote with breakdown
 */
function DynamicQuotePricing({ quote }: { quote: DynamicQuote }) {
  const [showBreakdown, setShowBreakdown] = useState(false);

  return (
    <Card className="border-blue-200 bg-blue-50">
      <CardContent className="p-6">
        {/* Header */}
        <div className="flex items-start gap-3 mb-4">
          <div className="p-2 bg-blue-100 rounded-lg">
            <DollarSign className="h-5 w-5 text-blue-700" aria-hidden="true" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-lg font-semibold text-blue-900">
                Estimated Quote
              </h3>
              <Badge variant="default" className="bg-blue-700">
                Dynamic
              </Badge>
            </div>
            <p className="text-sm text-blue-800">
              Based on your location and current availability
            </p>
          </div>
        </div>

        {/* Main Quote Display */}
        <div className="bg-white border border-blue-200 rounded-lg p-5 mb-3">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-3xl font-bold text-blue-700">
                {formatCents(quote.estimatedPriceCents)}
              </div>
              <div className="text-sm text-gray-600 mt-1">
                Estimated total
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-1.5 text-gray-700 mb-1">
                <Clock className="h-4 w-4" aria-hidden="true" />
                <span className="font-semibold">
                  {quote.estimatedDriveMinutes} min
                </span>
              </div>
              <div className="text-xs text-gray-600">
                {quote.estimatedDistanceMiles.toFixed(1)} miles away
              </div>
            </div>
          </div>

          {/* Expandable Breakdown */}
          <button
            type="button"
            onClick={() => setShowBreakdown(!showBreakdown)}
            className="w-full flex items-center justify-center gap-2 text-sm font-medium text-blue-700 hover:text-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded py-2 transition-colors"
            aria-expanded={showBreakdown}
            aria-controls="price-breakdown"
          >
            {showBreakdown ? (
              <>
                Hide Breakdown
                <ChevronUp className="h-4 w-4" aria-hidden="true" />
              </>
            ) : (
              <>
                View Breakdown
                <ChevronDown className="h-4 w-4" aria-hidden="true" />
              </>
            )}
          </button>

          {/* Breakdown Details */}
          {showBreakdown && (
            <div
              id="price-breakdown"
              className="mt-4 pt-4 border-t border-gray-200 space-y-2"
            >
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Base cost</span>
                <span className="font-medium text-gray-900">
                  {formatCents(quote.breakdown.baseCostCents)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Distance cost</span>
                <span className="font-medium text-gray-900">
                  {formatCents(quote.breakdown.distanceCostCents)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Time cost</span>
                <span className="font-medium text-gray-900">
                  {formatCents(quote.breakdown.timeCostCents)}
                </span>
              </div>
              <div className="flex justify-between text-sm pt-2 border-t border-gray-200">
                <span className="text-gray-600">Provider markup</span>
                <span className="font-medium text-gray-900">
                  {quote.breakdown.markupPercent}%
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Info Note */}
        <div className="flex items-start gap-2 text-xs text-blue-800 bg-blue-100 rounded-lg p-3">
          <Info className="h-4 w-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
          <p>
            This is an estimated quote. Final price will be confirmed at booking based on
            selected container size and material type.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Main PricingDisplay Component
 */
export function PricingDisplay({ type, fixedRates, dynamicQuote }: PricingDisplayProps) {
  if (type === 'fixed' && fixedRates) {
    return <FixedRatesPricing rates={fixedRates} />;
  }

  if (type === 'dynamic' && dynamicQuote) {
    return <DynamicQuotePricing quote={dynamicQuote} />;
  }

  return null;
}
